import React, {
    Fragment,
    FunctionComponent,
    Reducer,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react';

import { SemanticWIDTHS, Table, Select, Loader } from 'semantic-ui-react';
import styles from './team-predictions.module.scss';
import { useHttpService } from 'src/shared/hooks/use-http-service';
import { NflPredictionsService } from 'src/shared/services/nfl-predictions.service';
import { IGamePrediction, ITeamPrediction } from 'src/shared/models/interfaces/ipredictions';
import classNames from 'classnames';
import { addAlpha, colorize, tableReducer } from './table.utils';
import { get, max, range } from 'lodash';
import { NFL_TEAM_NICKNAMES } from 'src/shared/constants/nfl-teams';
import { NFLTeamAbbreviation } from 'src/shared/enums/nfl-abbreviations';
import { NFL_CONFERENCES, NFL_DIVISIONS, WEEK_FORECAST } from './options';
import { IconArrowDown, IconArrowUp, IconLineDashed } from '@tabler/icons-react';
import TeamSchedulePredictions from './team-schedule/team-schedule.component';

interface ColumnDefinition {
    key: string;
    title: string;
    render?: FunctionComponent<any>;
    columns?: SemanticWIDTHS;
    sorted?: boolean;
    sortable?: boolean;
    percent?: boolean;
    colorize?: boolean;
    fn?: (v: any) => any;
}

const TeamPredictions: FunctionComponent<{ season: number; currentWeek?: number }> = ({ season, currentWeek }) => {
    const predictionsService = useHttpService(NflPredictionsService);
    const [predictions, setPredictions] = useState<ITeamPrediction[]>([]);
    const [gamePredictions, setGamePredictions] = useState<IGamePrediction[]>([]);
    const [division, setDivision] = useState<string>();
    const [conference, setConference] = useState<string>();
    const [week, setWeek] = useState(currentWeek);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState<string>();

    useEffect(() => {
        if (currentWeek) setWeek(currentWeek);
    }, [currentWeek]);

    const [state, dispatch] = useReducer<
        Reducer<{ column?: any; data: ITeamPrediction[]; direction?: 'ascending' | 'descending'; type?: any }, any>
    >(tableReducer, {
        column: 'average_wins',
        data: predictions,
        direction: 'descending',
    });

    const { data } = state;

    const Team: FunctionComponent<{ value: string }> = ({ value }) => {
        return (
            <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', width: 130 }}>
                <img
                    src={require(`../../../../assets/images/nfl/${value}.png`)}
                    height={30}
                    style={{ marginRight: 5, paddingLeft: 5 }}
                />
                <div style={{ fontWeight: 800 }}>{NFL_TEAM_NICKNAMES[value as NFLTeamAbbreviation]}</div>
            </div>
        );
    };

    const columns: ColumnDefinition[] = [
        { key: 'rank', title: 'RANK' },
        { key: 'team_id', title: 'TEAM', render: Team },
        { key: 'division', title: 'DIVISION', columns: 2 },
        { key: 'twar_per_game', title: 'tWAR', sortable: true, fn: (v) => (v * 17).toFixed(2) },
        { key: 'average_wins', title: 'PROJ. WINS', columns: 2, sortable: true },
        { key: 'division_win_pct', title: 'WIN DIVISION', columns: 2, sortable: true, percent: true },
        { key: 'playoff_pct', title: 'MAKE PLAYOFFS', columns: 2, sortable: true, percent: true, colorize: true },
        {
            key: 'divisional_round_pct',
            title: 'MAKE DIVISIONAL',
            columns: 2,
            sortable: true,
            percent: true,
            colorize: true,
        },
        {
            key: 'conference_championship_pct',
            title: 'MAKE CONF. CHAMP.',
            columns: 2,
            sortable: true,
            percent: true,
            colorize: true,
        },
        { key: 'super_bowl_pct', title: 'MAKE SUPER BOWL', columns: 2, sortable: true, percent: true, colorize: true },
        {
            key: 'win_super_bowl_pct',
            title: 'WIN SUPER BOWL',
            columns: 2,
            sortable: true,
            percent: true,
            colorize: true,
        },
    ];

    useEffect(() => {
        dispatch({ type: 'UPDATE_DATA', data: predictions });
    }, [predictions]);

    const fetchData = useCallback(async () => {
        if (!week) return;
        try {
            setLoading(true);
            const predictions = await predictionsService.getTeamPredictions(season, week);
            setPredictions(predictions);
        } finally {
            setLoading(false);
        }

        const gamePredictions = await predictionsService.getGamePredictions(season, week);
        setGamePredictions(gamePredictions);
    }, [season, week]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const round = (v: any) => {
        if (v < 1) return v.toFixed(1);
        return v.toFixed(0);
    };

    const Header = () => (
        <Table.Header>
            <Table.Row
                style={{
                    position: 'sticky',
                    top: 0,
                    background: 'transparent',
                    zIndex: 50,
                }}
            >
                {columns.map((h) => {
                    if (h.key === 'rank')
                        return (
                            <Table.HeaderCell key={h.key} id="header">
                                RANK
                            </Table.HeaderCell>
                        );

                    return (
                        <Table.HeaderCell
                            key={h.key}
                            id="header"
                            className={classNames({
                                'sortable-header': h.sortable,
                                'sortable-header-ascending': state.direction === 'ascending' && state.column === h.key,
                                'sortable-header-descending':
                                    state.direction === 'descending' && state.column === h.key,
                            })}
                            width={h.columns}
                            style={{
                                minWidth: 70,
                                whiteSpace: 'break-spaces',
                                cursor: h.sortable ? 'pointer' : 'unset',
                            }}
                            onClick={() => {
                                if (!h.sortable) return;
                                dispatch({ type: 'CHANGE_SORT', column: h.key });
                            }}
                        >
                            {h.title}
                        </Table.HeaderCell>
                    );
                })}
            </Table.Row>
        </Table.Header>
    );

    const rows = useMemo(
        () =>
            data
                .filter((r) => (division ? r.division === division : true))
                .filter((r) => (conference ? r.conference === conference : true)),
        [data, division, conference],
    );

    const renderRank = (team: ITeamPrediction) => {
        const style: any = {};

        let Icon = null;
        style.color = 'grey';
        if (team.prevRank) {
            if (team.prevRank < team.rank) {
                style['backgroundColor'] = addAlpha('#FD9F9D', 0.3);
                style['color'] = '#90211E';
                Icon = IconArrowDown;
            } else if (team.prevRank > team.rank) {
                style['backgroundColor'] = addAlpha('#9dfeb7', 0.3);
                style['color'] = '#166E2E';
                Icon = IconArrowUp;
            } else if (team.prevRank === team.rank) {
                style['backgroundColor'] = addAlpha('#333', 0.3);
                style['color'] = '#166E2E';
                Icon = IconLineDashed;
            }
        }

        const rankDiff = team.prevRank ? Math.abs(team.prevRank - team.rank) : null;

        return (
            <Table.Cell key={`${team.team_id}_rank`}>
                <div className={styles.rankContainer}>
                    <div style={{ width: 25 }}>{team.rank}</div>
                    {team.prevRank ? (
                        <div className={styles.rankText} style={style}>
                            {Icon ? <Icon size={16} /> : null}
                            {rankDiff === 0 ? '' : rankDiff}
                        </div>
                    ) : null}
                </div>
            </Table.Cell>
        );
    };

    const onClick = (teamId: string) => {
        if (active === teamId) setActive(undefined);
        else setActive(teamId);
    };

    const Body = () => (
        <Table.Body>
            {rows.map((team: ITeamPrediction) => (
                <Fragment key={team.team_id}>
                    <Table.Row
                        className={styles.tableRow}
                        onClick={() => onClick(team.team_id)}
                        style={{ backgroundColor: active === team.team_id ? addAlpha('#b3b3b3', 0.2) : null }}
                    >
                        {columns.map((h) => {
                            const style: any = {};
                            if (h.colorize) {
                                const value = get(team, h.key);
                                const color = colorize(value, range(0, max(colorizeValues)));
                                style.backgroundColor = color;
                                style.color = '#000';
                            }

                            if (h.key === 'rank') return renderRank(team);

                            return (
                                <Table.Cell key={h.key} style={style}>
                                    {h.render ? (
                                        <h.render value={get(team, h.key)} />
                                    ) : h.fn ? (
                                        h.fn(get(team, h.key))
                                    ) : h.percent ? (
                                        `${round(get(team, h.key))}%`
                                    ) : (
                                        get(team, h.key)
                                    )}
                                </Table.Cell>
                            );
                        })}
                    </Table.Row>
                    {active === team.team_id ? (
                        <TeamSchedulePredictions
                            teamId={active}
                            week={week}
                            data={gamePredictions.filter(
                                (p) => active === p.home_team_id || active === p.visitor_team_id,
                            )}
                        />
                    ) : null}
                </Fragment>
            ))}
        </Table.Body>
    );

    const onWeekSelect = (v: any) => {
        if (v === 'current' && week !== currentWeek) {
            setPredictions([]);
            setWeek(currentWeek);
        } else if (week !== 1) {
            setPredictions([]);
            setWeek(1);
        }
    };

    const colorizeValues = data.map((d) => get(d, 'playoff_pct'));

    return (
        <div className={styles.container}>
            <div className={styles.row} style={{ marginTop: 20 }}>
                <div className={styles.row}>
                    <div className={styles.row}>
                        <div className={styles.label}>Conference</div>
                        <Select
                            clearable
                            placeholder="Select..."
                            className={styles.dropdown}
                            value={conference}
                            onChange={(_, { value }) => setConference(value as string)}
                            options={NFL_CONFERENCES.filter((c) => (division ? division.includes(c.text) : true))}
                        />
                    </div>
                    <div className={styles.row} style={{ marginLeft: 20 }}>
                        <div className={styles.label}>Division</div>
                        <Select
                            clearable
                            placeholder="Select..."
                            className={styles.dropdown}
                            value={division}
                            onChange={(_, { value }) => setDivision(value as string)}
                            options={NFL_DIVISIONS.filter((d) => (conference ? d.text.includes(conference) : true))}
                        />
                    </div>
                </div>
                <div className={styles.row}>
                    <div className={styles.label}>Forecast</div>
                    <Select
                        defaultValue="current"
                        className={styles.weekDropdown}
                        onChange={(_, { value }) => onWeekSelect(value)}
                        options={WEEK_FORECAST}
                    />
                </div>
            </div>
            {loading ? (
                <div className={styles.loader}>
                    <Loader active={true} size="big" inline />
                </div>
            ) : (
                <div className={styles.tableContainer}>
                    <Table
                        sortable
                        compact
                        role="grid"
                        textAlign="center"
                        aria-labelledby="header"
                        selectable
                        className="predictions-table"
                    >
                        <div id="predictions-table-body">
                            <Header />
                            <Body />
                        </div>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default TeamPredictions;
